/**
 * ResourceShareModal Component
 * Feature 020: Resource Sharing with Care Seeker Clients
 *
 * Modal for selecting care seeker clients to share a resource with via email.
 * Supports both Service Providers and Financial Advisors.
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Users, Search, X, AlertCircle } from 'lucide-react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useAdvisorClients } from '../hooks/useAdvisorClients';
import { useProviderClients } from '../hooks/useProviderClients';
import { useShareResource } from '../hooks/useShareResource';
import { NewClientInvitationForm } from '@/features/sp-client-management/components/NewClientInvitationForm';

type ResourceShareModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceId: string;
  resourceTitle: string;
};

type Client = {
  id: string;
  name: string;
  email: string;
  profilePhoto?: string;
};

export function ResourceShareModal({
  open,
  onOpenChange,
  resourceId,
  resourceTitle,
}: ResourceShareModalProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('existing');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());

  const isServiceProvider = user?.role === 'SERVICE_PROVIDER';
  const isFinancialAdvisor = user?.role === 'FINANCIAL_ADVISOR';

  // Fetch clients based on user role
  const {
    data: advisorClients = [],
    isLoading: isLoadingAdvisorClients,
  } = useAdvisorClients({ enabled: isFinancialAdvisor });

  const {
    data: providerClients = [],
    isLoading: isLoadingProviderClients,
  } = useProviderClients({ enabled: isServiceProvider });

  const { mutate: shareResource, isPending } = useShareResource();

  // Determine which client list to use
  const clients: Client[] = isFinancialAdvisor ? advisorClients : providerClients;
  const isLoading = isFinancialAdvisor ? isLoadingAdvisorClients : isLoadingProviderClients;

  // Filter clients by search term (real-time, case-insensitive, name only)
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) {
      return clients;
    }

    const term = searchTerm.toLowerCase();
    return clients.filter((client) =>
      client.name.toLowerCase().includes(term)
    );
  }, [clients, searchTerm]);

  // Get counts
  const totalClients = clients.length;
  const filteredCount = filteredClients.length;
  const selectedCount = selectedClientIds.size;

  // Handle select all (filtered clients only)
  const handleSelectAll = () => {
    const newSelected = new Set(selectedClientIds);
    filteredClients.forEach((client) => {
      newSelected.add(client.id);
    });
    setSelectedClientIds(newSelected);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedClientIds(new Set());
  };

  // Handle individual selection toggle
  const handleToggleClient = (clientId: string) => {
    const newSelected = new Set(selectedClientIds);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClientIds(newSelected);
  };

  // Handle submit
  const handleSubmit = () => {
    if (selectedClientIds.size === 0) return;

    shareResource(
      {
        resourceId,
        resourceTitle,
        recipientIds: Array.from(selectedClientIds),
      },
      {
        onSuccess: () => {
          // Reset and close
          setSelectedClientIds(new Set());
          setSearchTerm('');
          onOpenChange(false);
        },
        onError: (error) => {
          console.error('Failed to share resource:', error);
        },
      }
    );
  };

  // Handle close
  const handleClose = () => {
    if (!isPending) {
      setSelectedClientIds(new Set());
      setSearchTerm('');
      setActiveTab('existing');
      onOpenChange(false);
    }
  };

  // Handle new client invitation success
  const handleNewClientSuccess = () => {
    setActiveTab('existing');
    onOpenChange(false);
  };

  // Handle new client invitation cancel
  const handleNewClientCancel = () => {
    setActiveTab('existing');
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Share "{resourceTitle}"</DialogTitle>
          <DialogDescription>
            Select care seekers to share this resource with via email.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 shrink-0">
            <TabsTrigger value="existing">Existing Clients</TabsTrigger>
            <TabsTrigger value="new">New Client (not in app)</TabsTrigger>
          </TabsList>

          {/* Existing Clients Tab */}
          <TabsContent value="existing" className="flex-1 flex flex-col min-h-0 overflow-hidden space-y-4 mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">Fetching clients...</div>
              </div>
            ) : totalClients === 0 ? (
              // Empty state
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h4 className="text-gray-900 mb-2">No clients yet</h4>
                <p className="text-sm text-gray-500 mb-6 max-w-sm">
                  {isFinancialAdvisor
                    ? 'You need to have connected clients before you can share resources with them.'
                    : 'You need to have care seeker engagements before you can share resources with them.'}
                </p>
                <Button onClick={handleClose}>Close</Button>
              </div>
            ) : (
              <>
              {/* Selection Counter and Search */}
              <div className="space-y-3 border-b pb-4 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {selectedCount > 0 ? (
                      <span className="font-medium text-bquest-green">
                        {selectedCount} selected
                      </span>
                    ) : (
                      <span>
                        {filteredCount} care seeker{filteredCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {selectedCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeselectAll}
                      >
                        Deselect All
                      </Button>
                    )}
                    {filteredCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        Select All
                      </Button>
                    )}
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {selectedCount > 50 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      You've selected {selectedCount} care seekers. Consider breaking
                      this into smaller batches for better deliverability.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Client List */}
              <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-2">
                {filteredClients.map((client) => {
                  const isSelected = selectedClientIds.has(client.id);

                  return (
                    <label
                      key={client.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-bquest-green/5 border-bquest-green'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleClient(client.id)}
                        className="w-4 h-4 text-bquest-green border-gray-300 rounded focus:ring-bquest-green"
                      />

                      {client.profilePhoto ? (
                        <img
                          src={client.profilePhoto}
                          alt={client.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-bquest-green/10 flex items-center justify-center">
                          <span className="text-bquest-green font-semibold text-xs">
                            {client.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)}
                          </span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {client.name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">
                          {client.email}
                        </p>
                      </div>
                    </label>
                  );
                })}

                {filteredCount === 0 && searchTerm && (
                  <div className="text-center py-8 text-gray-500">
                    No care seekers found matching "{searchTerm}"
                  </div>
                )}
              </div>

              {/* Footer */}
              <DialogFooter className="border-t pt-4 shrink-0">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={selectedCount === 0 || isPending}
                  className="bg-bquest-green hover:bg-bquest-green/90"
                >
                  {isPending
                    ? 'Sharing...'
                    : `Share with ${selectedCount} care seeker${selectedCount !== 1 ? 's' : ''}`}
                </Button>
              </DialogFooter>
            </>
            )}
          </TabsContent>

          {/* New Client Tab */}
          <TabsContent value="new" className="flex-1 flex flex-col min-h-0 overflow-y-auto mt-4">
            <NewClientInvitationForm
              referralType="RESOURCE"
              referralId={resourceId}
              onSuccess={handleNewClientSuccess}
              onCancel={handleNewClientCancel}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
